/**
 * InsertBuildingBlockDialog — confirm surface for EVERY library-example
 * adapt insert (approved mocks: connect-inputs-default.html +
 * block-library-insert-model/insert-dialog-model-clean.html). A clean fit
 * renders the one-click variant (no mapping rows); candidates/conflicts add
 * the mapping section (FP-8: confirm, don't form). The adaptation model is
 * chosen HERE — the interface the action launches from — never silently
 * defaulted (operator decision 2026-08-05).
 *
 * The example is a TEMPLATE: «Insert & adapt with AI» is the PRIMARY action;
 * «Insert as-is» is the explicit verbatim escape (no AI — model ignored).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { BaseDialog } from '@modules/ui-components/dialogs/BaseDialog';
import { DialogHeader } from '@modules/ui-components/dialogs/DialogHeader';
import { DialogContent } from '@modules/ui-components/dialogs/DialogContent';
import { DialogFooter } from '@modules/ui-components/dialogs/DialogFooter';
import { AvailableModelSelector, useProjectDefaultAIModel } from '@modules/ai-settings';
import type { Theme } from '@mui/material/styles';
import type { ContractFitEntry } from '@modules/shared-types';
import type { CliBlockLibraryVariableMappingEntry } from '@modules/shared-types';
import { warningToneBg, warningToneText } from '../utils/warningTone';

export interface InsertBuildingBlockDecision {
  adapt: boolean;
  /** Only rows the operator changed away from the server auto-resolution. */
  variableMapping: Record<string, CliBlockLibraryVariableMappingEntry>;
  /** Present on adapt decisions only — the operator-chosen adaptation model. */
  model?: string;
}

export interface InsertBuildingBlockDialogProps {
  open: boolean;
  projectId: string;
  exampleName: string;
  strategyName?: string;
  fit: ContractFitEntry[];
  /** Display meta per example variable id (name/type from the envelope). */
  variableMeta: Record<string, { name?: string; type?: string }>;
  onSubmit: (decision: InsertBuildingBlockDecision) => void;
  onCancel: () => void;
}

const CREATE_VALUE = '::create::';

export const InsertBuildingBlockDialog: React.FC<InsertBuildingBlockDialogProps> = ({
  open,
  projectId,
  exampleName,
  strategyName,
  fit,
  variableMeta,
  onSubmit,
  onCancel,
}) => {
  // Row selection state: exampleVarId -> CREATE_VALUE | candidate varId.
  const [selection, setSelection] = useState<Record<string, string>>({});
  // Adaptation model — prefilled with the project default, chosen here
  // (never a silent provider switch). The default loads async: adopt it once
  // it arrives unless the operator already picked a model.
  const projectDefaultModel = useProjectDefaultAIModel(projectId);
  const [model, setModel] = useState(projectDefaultModel.value);

  useEffect(() => {
    if (!open) return;
    setSelection({});
    setModel(projectDefaultModel.value);
  }, [open, projectDefaultModel.value]);

  const inputs = useMemo(() => fit.filter((entry) => entry.kind === 'input'), [fit]);
  const outputs = useMemo(() => fit.filter((entry) => entry.kind === 'output'), [fit]);

  const currentValue = (entry: ContractFitEntry): string => {
    const chosen = selection[entry.exampleVarId];
    if (chosen) return chosen;
    if (entry.resolution === 'bind' && entry.targetVarId) return entry.targetVarId;
    return CREATE_VALUE;
  };

  const buildDecision = useCallback((adapt: boolean): InsertBuildingBlockDecision => {
    const variableMapping: Record<string, CliBlockLibraryVariableMappingEntry> = {};
    for (const entry of fit) {
      if (entry.resolution === 'needs-adapt') continue; // server-owned, existing-wins
      const value = currentValue(entry);
      const autoValue = entry.resolution === 'bind' && entry.targetVarId ? entry.targetVarId : CREATE_VALUE;
      if (value === autoValue) continue; // unchanged -> server auto-resolution
      variableMapping[entry.exampleVarId] = value === CREATE_VALUE
        ? { action: 'create' }
        : { action: 'bind', targetVarId: value };
    }
    return adapt && model
      ? { adapt, variableMapping, model }
      : { adapt, variableMapping };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fit, selection, model]);

  const renderRow = (entry: ContractFitEntry) => {
    const meta = variableMeta[entry.exampleVarId] ?? {};
    const needsAdapt = entry.resolution === 'needs-adapt';
    const referenced = entry.referencedValues ?? [];
    const candidates = entry.candidates ?? [];
    // The server auto-bound target MUST be in the option list: a Select whose
    // value is missing from its options renders BLANK (MUI out-of-range) —
    // design-review Must Fix #1.
    const autoBoundTarget = entry.resolution === 'bind'
      && entry.targetVarId
      && !candidates.some((candidate) => candidate.varId === entry.targetVarId)
      ? entry.targetVarId
      : null;

    return (
      <Box
        key={entry.exampleVarId}
        data-testid={`insert-block-row-${entry.exampleVarId}`}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.25,
          p: 1,
          border: 1,
          borderColor: needsAdapt ? 'warning.main' : 'divider',
          bgcolor: needsAdapt ? warningToneBg : undefined,
          borderRadius: 1,
        }}
      >
        <Box sx={{ minWidth: 150 }}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 12.5 }}>
            {entry.exampleVarId}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {meta.type ?? 'variable'}
            {referenced.length > 0 ? ` · ${referenced.join(' | ')}` : ''}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>→</Typography>
        <Box sx={{ flex: 1 }}>
          {needsAdapt ? (
            <Typography variant="caption" sx={{ color: warningToneText, lineHeight: 1.4 }}>
              ⚠ The strategy already defines <b>{entry.targetVarId ?? entry.exampleVarId}</b> and its
              values don&apos;t cover this block&apos;s references
              {referenced.length > 0 ? ` (${referenced.join(', ')})` : ''} — the existing definition
              stays untouched; AI adaptation rebinds the block to it.
            </Typography>
          ) : (
            <Select
              size="small"
              fullWidth
              value={currentValue(entry)}
              onChange={(event) => setSelection((prev) => ({
                ...prev,
                [entry.exampleVarId]: String(event.target.value),
              }))}
            >
              <MenuItem value={CREATE_VALUE}>Create new variable</MenuItem>
              {autoBoundTarget && (
                <MenuItem value={autoBoundTarget}>
                  {`Map to ${autoBoundTarget} ✓ values compatible`}
                </MenuItem>
              )}
              {candidates.map((candidate) => (
                <MenuItem
                  key={candidate.varId}
                  value={candidate.varId}
                  disabled={!candidate.valueCompatible}
                >
                  {`Map to ${candidate.varId}${candidate.valueCompatible ? ' ✓ values compatible' : ' — values incompatible'}`}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      mode="modal"
      data-testid="insert-building-block-dialog"
      slotProps={{ paper: { 'aria-label': 'Insert building block' } }}
    >
      <DialogHeader title="Insert building block" onClose={onCancel} />
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2">
            <b>{exampleName}</b>
            {strategyName ? (
              <Typography component="span" variant="body2" color="text.secondary">
                {' '}→ inserting into «{strategyName}»
              </Typography>
            ) : null}
          </Typography>

          {inputs.length > 0 && (
            <Typography variant="overline" color="text.secondary">Inputs ({inputs.length})</Typography>
          )}
          {inputs.map(renderRow)}
          {outputs.length > 0 && (
            <Typography variant="overline" color="text.secondary">Outputs ({outputs.length})</Typography>
          )}
          {outputs.map(renderRow)}

          <AvailableModelSelector
            projectId={projectId}
            value={model}
            onChange={setModel}
            label="AI Model"
            size="small"
            fullWidth
            disabled={projectDefaultModel.loading}
            testId="insert-block-model"
          />

          <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
            The example is a <b>template</b>: by default the AI adapts it to this strategy — rebinds
            the mapping above, rewrites enum values and copy to your product and tone (background
            task, structure &amp; layout kept). «Insert as-is» performs structural mapping only:
            it does not adapt copy, dataUsage, or asset semantics and may require manual repair.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogFooter
        primaryAction={{
          label: 'Insert & adapt with AI',
          onClick: () => onSubmit(buildDecision(true)),
          // Adapt needs a model: hold the primary until the project default
          // resolves (same rule as BlockScreenGenerationDialog canSubmit).
          disabled: projectDefaultModel.loading || !model,
          // The global dark theme renders every button glassy-transparent,
          // which collapses the primary/escape hierarchy (invariant #9) —
          // keep the template-default primary SOLID, same exemption pattern
          // as MuiButton-containedError in ThemeModeContext.
          sx: (theme: Theme) => (theme.palette.mode === 'dark' ? {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
              boxShadow: 'none',
            },
          } : {}),
          'data-testid': 'insert-block-adapt',
        } as never}
        secondaryAction={{
          label: 'Insert as-is',
          variant: 'text',
          onClick: () => onSubmit(buildDecision(false)),
          'data-testid': 'insert-block-as-is',
        } as never}
      />
    </BaseDialog>
  );
};

InsertBuildingBlockDialog.displayName = 'InsertBuildingBlockDialog';
